<template>
  <div
    ref="centerRef"
    class="notification-center relative"
  >
    <!-- Notification Bell Button with OverlayBadge -->
    <a
      ref="bellButton"
      class="text-surface-600 dark:text-surface-200 hover:text-surface-900 dark:hover:text-surface-0 hover:bg-surface-100 dark:hover:bg-surface-700 font-medium rounded-border cursor-pointer duration-150 transition-colors p-2"
      @click="toggle"
    >
      <OverlayBadge
        v-if="unreadCount > 0"
        :value="unreadCount > 9 ? '9+' : unreadCount.toString()"
        severity="danger"
        class="mr-2 lg:mr-0"
      >
        <i class="pi pi-bell text-base lg:!text-2xl leading-none" />
      </OverlayBadge>
      <i
        v-else
        class="pi pi-bell text-base lg:!text-2xl leading-none"
      />
      <span class="block lg:hidden font-medium ml-2">Notifications</span>
    </a>

    <!-- Notification Panel using PrimeVue Popover -->
    <Popover
      ref="popover"
      :show-close-icon="false"
      position="bottom"
      class="notifications-popover"
      @show="onPopoverShow"
    >
      <div class="p-0 fixed-width-panel">
        <!-- Panel Header -->
        <div class="p-4 text-surface-900 dark:text-surface-0 font-medium border-b border-surface flex justify-between items-center">
          <span>
            Notifications
            <span
              v-if="unreadCount > 0"
              class="ml-2 text-sm text-primary"
            >
              {{ unreadCount }} unread
            </span>
          </span>
          <Button
            v-if="unreadCount > 0"
            class="text-sm text-primary hover:text-primary-600"
            text
            label="Mark all as read"
            @click="handleMarkAllAsRead"
          />
        </div>

        <!-- Empty state when no notifications -->
        <div
          v-if="notifications.length === 0"
          class="p-6 text-center empty-state-container"
        >
          <i class="pi pi-bell-slash text-4xl text-surface-300 dark:text-surface-600 mb-3" />
          <p class="text-surface-500 dark:text-surface-400">
            No notifications to display
          </p>
        </div>

        <!-- Notifications List -->
        <NotificationsList
          v-else
          max-height="350px"
          :compact="true"
          @notification-click="handleNotificationClick"
        />

        <!-- Panel Footer -->
        <div class="p-2 border-t border-surface text-center">
          <router-link
            to="/notifications"
            class="text-sm text-primary hover:text-primary-600 block py-1"
            @click="hidePopover"
          >
            View all notifications
          </router-link>
        </div>
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useNotifications } from '~/composables/useNotifications'
import type { Notification } from '~/shared/types/notification'

const centerRef = ref<HTMLElement | null>(null)
const bellButton = ref<HTMLElement | null>(null)
const popover = ref() // Reference to the Popover component

const {
  unreadCount,
  markAllAsRead,
  notificationsArray: notifications,
} = useNotifications()

// Toggle notification popover
const toggle = (event: Event) => {
  if (popover.value) {
    popover.value.toggle(event)
  }
}

// Call alignOverlay after popover is shown
const onPopoverShow = () => {
  // Allow DOM to update before realigning
  setTimeout(() => {
    if (popover.value) {
      popover.value.alignOverlay()
    }
  }, 0)
}

// Hide popover
const hidePopover = () => {
  if (popover.value) {
    popover.value.hide()
  }
}

// Handle notification click
const handleNotificationClick = (notification: Notification) => {
  // Use the useNotifications composable to handle the navigation
  const { handleNotificationClick: composableHandler } = useNotifications()
  composableHandler(notification)

  // Close the panel after clicking a notification
  hidePopover()
}

// Mark all as read
const handleMarkAllAsRead = async () => {
  await markAllAsRead()
}
</script>

<style scoped>
/* Override Popover styling for notifications */
:deep(.p-popover) {
  max-width: 95vw;
}

/* Fix arrow position */
:deep(.p-popover.p-popover-bottom .p-popover-arrow) {
  left: calc(var(--overlayArrowLeft, 0) + 12px) !important;
}

/* Add padding to bell button to make it easier to click */
.notification-center a {
  padding: 0.5rem;
}

/* Set consistent width for the notification panel */
.fixed-width-panel {
  width: 380px;
  min-width: 320px;
}

/* Empty state styling */
.empty-state-container {
  min-height: 150px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

/* On mobile, make popover wider but keep size consistent */
@media (max-width: 640px) {
  .fixed-width-panel {
    width: 90vw;
    min-width: 90vw;
  }

  :deep(.p-popover-content) {
    width: 90vw !important;
  }
}
</style>
